import { Button, FormLayout, TextInput } from 'components/common'
import { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ConfigurationWithId } from 'types/configuration'
import { handleError } from 'utils/helpers/handleError'
import {
  ApproverManagerConfigFormFields,
  approvalManagerSchema,
} from 'utils/schema/approverManagerConfigSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { usePageAccess } from 'hooks'
import { PlusIcon, CloseIcon } from 'components/icons'
import { updateApprovalManagers } from 'utils/api/configApi'

type Props = {
  approvalManagers?: ConfigurationWithId['approvalManagers']
}

const ApprovalManagerConfigForm = ({ approvalManagers }: Props) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ApproverManagerConfigFormFields>({
    resolver: yupResolver(approvalManagerSchema),
    defaultValues: {
      approvalManagers: approvalManagers || [],
    },
  })

  const [loading, setLoading] = useState(false)
  const { isEditor } = usePageAccess()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'approvalManagers',
  })

  const handleChangeConfigurations = async (data: any) => {
    const configToast = toast.loading('Updating Approvers config...')
    setLoading(true)
    try {
      await updateApprovalManagers(data)
      toast.success('Approvers config updated successfully.', {
        id: configToast,
      })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update Approvers config.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleChangeConfigurations)}>
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Approval managers
          </h3>
          <p className="mt-0.5 text-sm text-gray-500">
            Managers who can approve user authentication
          </p>
        </div>

        {isEditor && (
          <Button
            type="button"
            icon={<PlusIcon />}
            onClick={() => append({ name: '', phoneNumber: '' })}
            buttonText="Add Manager"
          />
        )}
      </div>

      {/* Manager rows */}
      <FormLayout className="space-y-2.5">
        <>
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-3">
              <TextInput
                disabled={!isEditor}
                label="Name"
                register={register}
                name={`approvalManagers.${index}.name`}
                error={errors?.approvalManagers?.[index]?.name?.message}
                className="flex-1"
              />

              <TextInput
                disabled={!isEditor}
                label="Mobile number"
                placeholder="9715XXXXXXXX"
                register={register}
                name={`approvalManagers.${index}.phoneNumber`}
                error={errors?.approvalManagers?.[index]?.phoneNumber?.message}
                className="flex-1"
              />

              {isEditor && (
                <Button
                  type="button"
                  onClick={() => fields.length !== 1 && remove(index)}
                  disabled={fields.length === 1}
                  variant="custom"
                  className="mt-2.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                  icon={<CloseIcon />}
                  aria-label="Remove manager"
                />
              )}
            </div>
          ))}

          {fields.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
              <p className="text-sm text-gray-400">No managers added yet.</p>
            </div>
          )}

          {errors.approvalManagers?.root?.message && (
            <p className="mt-1 text-xs text-error">
              {errors.approvalManagers.root.message}
            </p>
          )}
        </>
      </FormLayout>

      {/* Footer actions */}
      <div className="mt-4 flex items-center justify-end gap-2 border-t border-gray-100">
        <Button
          type="button"
          variant="outline"
          onClick={() => reset()}
          buttonText="Reset"
        />

        <Button loading={loading} buttonText="Save changes" />
      </div>
    </form>
  )
}

export default ApprovalManagerConfigForm
